import { type Facing, mulberry32, type Rng, TERRACE, valueNoise } from '@nine/shared';
import { box, canvas, ctx2d, ell, hexRgb, outline } from './pixel';
import { alongAxis, block3, crisp, poly, type Proj, type Pt, seg, shade, type Sprite } from './scenery';

/**
 * Temple terraces (ลานยกพื้นกับบันได): a raised stone terrace with its retaining wall, the stairs up
 * its front between stone cheeks, a balustrade along the top and a paved courtyard before it —
 * after the great stairs of a mountain temple's hall. Five looks, sharing the one build; each
 * pairs a kind of stair with a kind of paving. On a map the terrace is a structure (its size and
 * heights in TERRACE, shared) and its courtyard is paved ground tiles; the studio shows each style
 * whole, courtyard and all.
 */
export type Paving = 'grid' | 'bond' | 'crazy' | 'marble' | 'snow';

export interface TerraceStyle {
  id: string;
  label: string;
  /** How the courtyard and the terrace top are paved. */
  paving: Paving;
  /** Paving stone, its joints, the wall and step stone (hex). */
  floor: string;
  joint: string;
  stone: string;
  /** The retaining wall: dressed courses, or rough rubble with moss in the joints. */
  wall: 'ashlar' | 'rubble';
  /** The studio sample: its length along the wall and its stairs as [from, to) tile spans along it. */
  sample: { len: number; stairs: readonly (readonly [number, number])[] };
  /** Cheeks either side of each stair and a rail along the top: solid parapets, marble balusters, none. */
  rail: 'parapet' | 'baluster' | 'none';
  /** A carved dragon ramp (御路) up the middle of the centre stair. */
  ramp?: boolean;
  /** Snow on every tread, cap and the courtyard. */
  snowy?: boolean;
  /** Moss in the joints and steps worn uneven. */
  worn?: boolean;
  /** A bronze incense burner at the foot of the stairs. */
  censer?: boolean;
}

/** Three stairs along an 11-tile wall: a wide one in the middle, a narrow one at each end. */
const TRIPLE = [
  [0, 2],
  [4, 7],
  [9, 11],
] as const;

export const TERRACE_STYLES: readonly TerraceStyle[] = [
  {
    id: 'granite',
    label: 'บันไดหินแกรนิต · ลานหินสี่เหลี่ยม',
    paving: 'grid',
    floor: '#8a8a84',
    joint: '#5e5e58',
    stone: '#9a988e',
    wall: 'ashlar',
    sample: { len: 9, stairs: [[3, 6]] },
    rail: 'parapet',
  },
  {
    id: 'triple',
    label: 'บันไดสามสาย · ลานปูอิฐสลับ',
    paving: 'bond',
    floor: '#9c9688',
    joint: '#6a645a',
    stone: '#a29e92',
    wall: 'ashlar',
    sample: { len: 11, stairs: TRIPLE },
    rail: 'parapet',
    censer: true,
  },
  {
    id: 'mountain',
    label: 'บันไดหินภูเขา · ลานหินกาบ',
    paving: 'crazy',
    floor: '#848a78',
    joint: '#4a5040',
    stone: '#8e8c7c',
    wall: 'rubble',
    sample: { len: 9, stairs: [[3, 6]] },
    rail: 'none',
    worn: true,
  },
  {
    id: 'marble',
    label: 'บันไดหินอ่อนทางมังกร · ลานหินอ่อน',
    paving: 'marble',
    floor: '#dcdad2',
    joint: '#a8a49a',
    stone: '#e6e4dc',
    wall: 'ashlar',
    sample: { len: 9, stairs: [[3, 6]] },
    rail: 'baluster',
    ramp: true,
  },
  {
    id: 'frost',
    label: 'บันไดหิมะ (ยอดหมอก) · ลานหินเปียกหิมะ',
    paving: 'snow',
    floor: '#5c605e',
    joint: '#3a3e3c',
    stone: '#7a7c76',
    wall: 'rubble',
    sample: { len: 11, stairs: TRIPLE },
    rail: 'parapet',
    snowy: true,
    censer: true,
  },
];

/** `shade` as a hex colour, to shade again. */
const darken = (hex: string, m: number): string =>
  '#' +
  shade(hex, m)
    .slice(4, -1)
    .split(',')
    .map((v) => (+v).toString(16).padStart(2, '0'))
    .join('');

const SNOW = '#eef2f4';
const SNOW_S = '#c8d2da';

/** Terrace size (shared with the rules): `DT` deep, `HT` high; stairs run `RUN` out in `STEPS` steps. */
const DT = TERRACE.depth;
const HT = TERRACE.height;
const RUN = TERRACE.run;
const STEPS = TERRACE.steps;
/** How far the studio sample's courtyard reaches out past the foot of the stairs. */
const COURT = 3.5;
const RAIL = 12;

/** A box with its own top colour (snow on the treads). */
function slab(
  g: CanvasRenderingContext2D,
  P: Proj,
  [x0, y0, x1, y1]: readonly [number, number, number, number],
  z0: number,
  z1: number,
  col: string,
  top?: string,
): void {
  block3(g, P, [x0, y0, x1, y1], z0, z1, col);
  if (top) poly(g, [P(x0, y0, z1), P(x1, y0, z1), P(x1, y1, z1), P(x0, y1, z1)], top);
}

/** Paving over a rectangle at height z, clipped to it, in the style's pattern. */
function pave(
  g: CanvasRenderingContext2D,
  P: Proj,
  [x0, y0, x1, y1]: readonly [number, number, number, number],
  z: number,
  s: TerraceStyle,
  r: Rng,
): void {
  const Q = (x: number, y: number) => P(x, y, z);
  poly(g, [Q(x0, y0), Q(x1, y0), Q(x1, y1), Q(x0, y1)], s.joint);
  g.save();
  g.beginPath();
  [Q(x0, y0), Q(x1, y0), Q(x1, y1), Q(x0, y1)].forEach(([x, y], k) => (k ? g.lineTo(x, y) : g.moveTo(x, y)));
  g.closePath();
  g.clip();
  const stone = (pts: Pt[], m = 0.9 + r() * 0.16) => poly(g, pts, shade(s.floor, m));
  /** A cell inset by the joint, as a quad. */
  const cell = (a: number, b: number, c: number, d: number, gap = 0.035): Pt[] => [
    Q(a + gap, b + gap),
    Q(c - gap, b + gap),
    Q(c - gap, d - gap),
    Q(a + gap, d - gap),
  ];
  if (s.paving === 'grid' || s.paving === 'snow') {
    const k = 0.5;
    for (let y = y0; y < y1; y += k)
      for (let x = x0; x < x1; x += k) {
        stone(cell(x, y, x + k, y + k));
        // Snow lies in drifts (noise) and in the joints; the wet stone shows dark between.
        if (s.paving === 'snow') {
          const n = valueNoise(x * 0.32 + 3, y * 0.32 + 7) * 0.8 + valueNoise(x * 1.3, y * 1.3 + 5) * 0.2;
          if (n > 0.55) poly(g, cell(x, y, x + k, y + k, 0.01), n > 0.66 ? SNOW : SNOW_S);
          else if (n > 0.47) poly(g, cell(x + k * 0.4, y + k * 0.3, x + k, y + k, 0.01), SNOW_S);
        }
      }
  } else if (s.paving === 'bond') {
    // Long stones laid across, each row shifted half a stone.
    const d = 0.375;
    const l = 0.75;
    for (let y = y0, row = 0; y < y1; y += d, row++)
      for (let x = x0 - (row % 2) * (l / 2); x < x1; x += l) stone(cell(x, y, x + l, y + d, 0.03));
  } else if (s.paving === 'crazy') {
    // Flagstones of every shape: a grid with its corners pushed about, moss in some joints.
    const k = 0.6;
    const nx = Math.ceil((x1 - x0) / k) + 1;
    const ny = Math.ceil((y1 - y0) / k) + 1;
    const pts: Pt[][] = [];
    for (let j = 0; j <= ny; j++) {
      pts.push([]);
      for (let i = 0; i <= nx; i++)
        pts[j]!.push([x0 + i * k + (r() - 0.5) * 0.3, y0 + j * k + (r() - 0.5) * 0.3]);
    }
    for (let j = 0; j < ny; j++)
      for (let i = 0; i < nx; i++) {
        const q = [pts[j]![i]!, pts[j]![i + 1]!, pts[j + 1]![i + 1]!, pts[j + 1]![i]!];
        const cx = (q[0][0] + q[1][0] + q[2][0] + q[3][0]) / 4;
        const cy = (q[0][1] + q[1][1] + q[2][1] + q[3][1]) / 4;
        stone(
          q.map(([x, y]) => Q(cx + (x - cx) * 0.9, cy + (y - cy) * 0.9)),
          0.86 + r() * 0.22,
        );
        if (r() < 0.35) {
          const [mx, my] = Q(q[1][0], q[1][1]);
          box(g, Math.round(mx) - 1, Math.round(my), 3, 1, '#5e7a3a');
          box(g, Math.round(mx), Math.round(my) - 1, 1, 1, '#7a9a48');
        }
      }
  } else {
    // Great marble slabs with faint veins, a border of grey stone round the edge.
    const b = 0.3;
    for (let y = y0; y < y1; y += b) {
      stone(cell(x0, y, x0 + b, y + b), 0.78);
      stone(cell(x1 - b, y, x1, y + b), 0.78);
    }
    for (let x = x0 + b; x < x1 - b; x += b) stone(cell(x, y1 - b, x + b, y1), 0.78);
    for (let y = y0; y < y1 - b; y += 1)
      for (let x = x0 + b; x < x1 - b; x += 1) {
        const e = Math.min(x + 1, x1 - b);
        const f = Math.min(y + 1, y1 - b);
        stone(cell(x, y, e, f, 0.02), 0.96 + r() * 0.08);
        const v0: Pt = [x + r() * (e - x), y + 0.1];
        const v1: Pt = [x + r() * (e - x), f - 0.1];
        seg(g, Q(...v0), Q((v0[0] + v1[0]) / 2 + 0.15, (v0[1] + v1[1]) / 2), shade(s.floor, 0.86));
        seg(g, Q((v0[0] + v1[0]) / 2 + 0.15, (v0[1] + v1[1]) / 2), Q(...v1), shade(s.floor, 0.86));
      }
  }
  g.restore();
}

/** A wall face of stone blocks: `Q(u, z)` maps along-the-face `u` and height `z` to the sprite. */
function masonry(
  g: CanvasRenderingContext2D,
  Q: (u: number, z: number) => Pt,
  u0: number,
  u1: number,
  z0: number,
  z1: number,
  col: string,
  s: TerraceStyle,
  r: Rng,
): void {
  const joint = shade(col, 0.6);
  poly(g, [Q(u0, z0), Q(u1, z0), Q(u1, z1), Q(u0, z1)], joint);
  const rough = s.wall === 'rubble';
  for (let z = z0, row = 0; z < z1; row++) {
    const h = Math.min(z1 - z, rough ? 5 + Math.floor(r() * 5) : 7);
    let u = u0 - (rough ? r() * 0.3 : (row % 2) * 0.3);
    while (u < u1) {
      const l = rough ? 0.25 + r() * 0.5 : 0.6;
      const a = Math.max(u0, u);
      const b = Math.min(u1, u + l);
      if (b > a) {
        poly(
          g,
          [Q(a + 0.02, z + 1), Q(b - 0.02, z + 1), Q(b - 0.02, z + h), Q(a + 0.02, z + h)],
          shade(col, 0.88 + r() * 0.18),
        );
        if (s.worn && r() < 0.18) {
          const [mx, my] = Q(a + (b - a) * r(), z + 1);
          box(g, Math.round(mx), Math.round(my) - 1, 2, 2, '#5a7a38');
        }
        if (s.snowy && r() < 0.3) {
          const [mx, my] = Q(a + 0.05, z + h);
          box(g, Math.round(mx), Math.round(my), Math.max(2, Math.round((b - a) * 10)), 1, SNOW_S);
        }
      }
      u += l;
    }
    z += h;
  }
}

/** A parapet sloping from (yA, zA) to (yB, zB) between x0 and x1: top, end and the +x side. */
function sloped(
  g: CanvasRenderingContext2D,
  P: Proj,
  x0: number,
  x1: number,
  yA: number,
  yB: number,
  zA: number,
  zB: number,
  col: string,
  top: string,
): void {
  // The +x side faces the key light (up and to the right); the end at the foot, toward the viewer
  // and left, is in shade.
  poly(g, [P(x1, yA, 0), P(x1, yB, 0), P(x1, yB, zB), P(x1, yA, zA)], shade(col, 1));
  // The end at the foot shows only when the foot is the near end.
  if (yB > yA) poly(g, [P(x0, yB, 0), P(x1, yB, 0), P(x1, yB, zB), P(x0, yB, zB)], shade(col, 0.8));
  poly(g, [P(x0, yA, zA), P(x1, yA, zA), P(x1, yB, zB), P(x0, yB, zB)], top);
}

/** A row of marble balusters with a handrail, from (xA, yA, zA) to (xB, yB, zB). */
function balusters(
  g: CanvasRenderingContext2D,
  P: Proj,
  a: readonly [number, number, number],
  b: readonly [number, number, number],
  col: string,
): void {
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const n = Math.max(1, Math.round(len / 0.55));
  const at = (t: number, dz: number) =>
    P(a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t + dz);
  // The panels between the posts, then the posts with their round caps, then the rail on top.
  for (let k = 0; k < n; k++) {
    const p = at(k / n, 2);
    const q = at((k + 1) / n, 2);
    poly(g, [p, q, [q[0], q[1] - RAIL + 4], [p[0], p[1] - RAIL + 4]], shade(col, 0.9));
    const m = at((k + 0.5) / n, RAIL / 2);
    box(g, Math.round(m[0]) - 1, Math.round(m[1]) - 2, 3, 3, shade(col, 0.72));
  }
  seg(g, at(0, RAIL - 2), at(1, RAIL - 2), shade(col, 1.04), 2);
  for (let k = 0; k <= n; k++) {
    const [px, py] = at(k / n, 0);
    box(g, Math.round(px) - 1, Math.round(py) - RAIL - 2, 3, RAIL + 2, shade(col, 0.96));
    box(g, Math.round(px) - 1, Math.round(py) - RAIL - 2, 1, RAIL + 2, shade(col, 0.76));
    ell(g, px + 0.5, py - RAIL - 3, 2, 2, col);
  }
}

/** A bronze incense burner on its plinth, smoke curling up. */
function censer(g: CanvasRenderingContext2D, P: Proj, x: number, y: number): void {
  slab(g, P, [x - 0.3, y - 0.3, x + 0.3, y + 0.3], 0, 5, '#7a786e');
  const [cx, cy] = P(x, y, 5);
  ell(g, cx, cy - 7, 8, 6, '#5a4a2a');
  ell(g, cx - 2, cy - 9, 4, 3, '#8a7440');
  box(g, Math.round(cx) - 9, Math.round(cy) - 13, 18, 3, '#6a5630');
  box(g, Math.round(cx) - 10, Math.round(cy) - 16, 3, 4, '#5a4a2a');
  box(g, Math.round(cx) + 7, Math.round(cy) - 16, 3, 4, '#5a4a2a');
  box(g, Math.round(cx) - 6, Math.round(cy) - 2, 2, 4, '#4a3a20');
  box(g, Math.round(cx) + 4, Math.round(cy) - 2, 2, 4, '#4a3a20');
  for (let k = 0; k < 9; k++)
    box(
      g,
      Math.round(cx + Math.sin(k * 0.9) * 3),
      Math.round(cy) - 18 - k * 4,
      2,
      3,
      k % 2 ? '#d8d8d4' : '#c4c4c0',
    );
}

/** Where a terrace stands: its length along the wall, its stairs, the side they come down. */
export interface TerraceLayout {
  len: number;
  stairs: readonly (readonly [number, number])[];
  facing: Facing;
  /** Draw a courtyard of paving before the stairs (the studio; on a map the ground is paved). */
  court?: boolean;
}

/**
 * The terrace and its stairs in one style, laid out as on the map: the anchor is the footprint's
 * north corner. Drawn with the wall along x, stairs toward +y (s) or −y (n); e and w are the
 * mirror images of those.
 */
export function terraceSprite(s: TerraceStyle, lay: TerraceLayout): Sprite {
  const ns = lay.facing === 'n' || lay.facing === 's';
  const front = lay.facing === 's' || lay.facing === 'e';
  const sp = drawTerrace(s, lay.len, lay.stairs, front, !!lay.court && front);
  return ns ? sp : alongAxis(sp, 'y');
}

/** The studio sample of a style: stairs toward the viewer, with its courtyard. */
export const terraceSample = (s: TerraceStyle): Sprite =>
  terraceSprite(s, { ...s.sample, facing: 's', court: true });

function drawTerrace(
  s: TerraceStyle,
  L: number,
  stairs: readonly (readonly [number, number])[],
  front: boolean,
  court: boolean,
): Sprite {
  const m = 16;
  const depth = DT + RUN + (court ? COURT : 0);
  const W = (L + depth) * 16 + 2 * m;
  const ax = depth * 16 + m;
  const ay = HT + RAIL + 20 + m;
  const H = Math.ceil(ay + (L + depth) * 8 + m);
  const c = canvas(W, H);
  const g = ctx2d(c);
  const P: Proj = (x, y, z) => [ax + (x - y) * 16, ay + (x + y) * 8 - z];
  const r = mulberry32(s.id.length * 977 + s.paving.charCodeAt(0) + L);
  const capTop = (col: string) => (s.snowy ? SNOW : shade(col, 1.12));
  // The platform's near and far edges (y), and the edge the stairs come down from.
  const [ty0, ty1] = front ? [0, DT] : [RUN, RUN + DT];
  const edge = front ? ty1 : ty0;
  const gaps = [...stairs].sort((a, b) => a[0] - b[0]);
  const cheek = s.rail === 'none' ? 0 : 0.28;
  const rise = HT / STEPS;
  const tread = RUN / STEPS;

  /** One stair: cheeks (inside its span) either side of the steps, west to east. */
  const stair = ([a, b]: readonly [number, number]) => {
    const dir = front ? 1 : -1;
    const foot = edge + dir * RUN;
    const cheekAt = (x0: number, x1: number) => {
      const top = edge - dir * 0.2;
      const end = foot + dir * 0.15;
      if (s.rail === 'parapet') sloped(g, P, x0, x1, top, end, HT + RAIL, RAIL - 2, s.stone, capTop(s.stone));
      if (s.rail === 'baluster') {
        sloped(g, P, x0, x1, top, end, HT + 3, 3, s.stone, capTop(s.stone));
        balusters(g, P, [(x0 + x1) / 2, edge - dir * 0.1, HT + 3], [(x0 + x1) / 2, foot, 3], s.stone);
      }
      // A newel post at the foot.
      if (s.rail !== 'none') {
        const [p0, p1] = front ? [foot, foot + 0.3] : [foot - 0.3, foot];
        slab(g, P, [x0 - 0.04, p0, x1 + 0.04, p1], 0, RAIL + 6, s.stone, capTop(s.stone));
      }
    };
    if (cheek) cheekAt(a, a + cheek);
    const sa = a + cheek;
    const sb = b - cheek;
    for (let k = 0; k < STEPS; k++) {
      // Step k from the bottom reaches from the wall out to its nose.
      const nose = edge + dir * tread * (STEPS - k);
      const [y0, y1] = front ? [edge - 0.02, nose] : [nose, edge + 0.02];
      // Worn steps: each a little out of line.
      const j0 = s.worn ? (r() - 0.5) * 0.25 : 0;
      const j1 = s.worn ? (r() - 0.5) * 0.25 : 0;
      const z = (k + 1) * rise;
      slab(
        g,
        P,
        [sa + j0, y0, sb + j1, y1],
        k * rise,
        z,
        s.stone,
        s.snowy ? undefined : shade(s.stone, 1.14),
      );
      if (s.snowy) {
        // Snow on the back of each tread, swept off the middle by the feet going up.
        const back = nose - dir * tread;
        const yf = nose - dir * tread * 0.25;
        poly(g, [P(sa + j0, back, z), P(sb + j1, back, z), P(sb + j1, yf, z), P(sa + j0, yf, z)], SNOW);
        const mid = (sa + sb) / 2;
        poly(
          g,
          [P(mid - 0.3, back, z), P(mid + 0.3, back, z), P(mid + 0.3, yf, z), P(mid - 0.3, yf, z)],
          SNOW_S,
        );
      }
      if (s.worn && r() < 0.3) {
        const [px, py] = P(sa + j0 + 0.1, nose, z);
        box(g, Math.round(px), Math.round(py) - 1, 3, 2, '#5a7a38');
      }
    }
    if (s.ramp && front) {
      // The dragon ramp: a carved slab laid up the middle, clouds and a coiling dragon in relief.
      const mid = (a + b) / 2;
      const w = (b - a) * 0.17;
      const pts: Pt[] = [
        P(mid - w, edge, HT + 1),
        P(mid + w, edge, HT + 1),
        P(mid + w, foot, 2),
        P(mid - w, foot, 2),
      ];
      poly(g, pts, shade(s.stone, 1.02));
      seg(g, pts[1]!, pts[2]!, shade(s.stone, 0.7), 2);
      seg(g, pts[0]!, pts[3]!, shade(s.stone, 0.8), 1);
      for (let k = 1; k < 10; k++) {
        const t = k / 10;
        const [qx, qy] = P(mid + Math.sin(t * 9) * w * 0.55, edge + RUN * t, HT * (1 - t) + 2);
        box(g, Math.round(qx) - 1, Math.round(qy) - 1, 3, 2, k % 3 ? '#b8b4a8' : '#d8a840');
      }
    }
    if (cheek) cheekAt(b - cheek, b);
  };

  // A rail along an edge of the platform top (x from a to b at y, or along y at x).
  const railX = (a: number, b: number, y: number) => {
    if (s.rail === 'parapet') slab(g, P, [a, y - 0.17, b, y], HT, HT + RAIL, s.stone, capTop(s.stone));
    if (s.rail === 'baluster') balusters(g, P, [a, y - 0.1, HT], [b, y - 0.1, HT], s.stone);
  };
  const railY = (x: number, a: number, b: number) => {
    if (s.rail === 'parapet') slab(g, P, [x - 0.17, a, x, b], HT, HT + RAIL, s.stone, capTop(s.stone));
    if (s.rail === 'baluster') balusters(g, P, [x - 0.1, a, HT], [x - 0.1, b, HT], s.stone);
  };
  /** The runs of an edge left between the stairs. */
  const between = (): [number, number][] => {
    const out: [number, number][] = [];
    let from = 0;
    for (const [a, b] of gaps) {
      if (a > from) out.push([from, a]);
      from = b;
    }
    if (from < L) out.push([from, L]);
    return out;
  };

  if (court) pave(g, P, [0, 0, L, depth], 0, s, r);
  // Stairs down the far side stand behind the platform: drawn first, their cheeks peeping over.
  if (!front) gaps.forEach(stair);
  slab(g, P, [0, ty0, L, ty1], 0, HT, s.stone);
  masonry(g, (u, z) => P(u, ty1, z), 0, L, 0, HT - 4, s.stone, s, r);
  masonry(g, (u, z) => P(L, u, z), ty0, ty1, 0, HT - 4, darken(s.stone, 0.74), s, r);
  // A coping course along the top of the wall, the paving on top.
  slab(g, P, [-0.04, ty0 - 0.04, L + 0.04, ty1 + 0.04], HT - 4, HT, s.stone, capTop(s.stone));
  pave(g, P, [0.08, ty0 + 0.08, L - 0.08, ty1 - 0.08], HT, s, r);
  // Rails round the top, far edges first, open where the stairs come up.
  if (s.rail !== 'none') {
    for (const [a, b] of front ? [[0, L] as [number, number]] : between()) railX(a, b, ty0 + 0.17);
    railY(0.17, ty0, ty1);
    railY(L, ty0, ty1);
    for (const [a, b] of front ? between() : [[0, L] as [number, number]]) railX(a, b, ty1);
  }
  if (front) gaps.forEach(stair);

  if (court && s.censer) {
    const mid = gaps[Math.floor(gaps.length / 2)]!;
    censer(g, P, (mid[0] + mid[1]) / 2, DT + RUN + 1.6);
  }
  outline(crisp(c), '#1c1612');
  if (court && s.snowy) {
    // Pigeons pecking about the courtyard, as in the snowy morning at the temple.
    for (let k = 0; k < 9; k++) {
      const [px, py] = P(L / 2 + 1 + r() * 2.8, DT + RUN + 0.6 + r() * 2.6, 0);
      box(g, Math.round(px), Math.round(py) - 3, 4, 2, k % 3 ? '#8a8e94' : '#e4e6ea');
      box(g, Math.round(px) + (k % 2 ? 3 : -1), Math.round(py) - 4, 2, 2, '#4a4e56');
      box(g, Math.round(px) + 1, Math.round(py) - 1, 1, 1, '#c86a4a');
    }
  }
  return { img: c, ax, ay };
}

const hash = (x: number, y: number): number => {
  const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return n - Math.floor(n);
};

/**
 * The colour of a style's paving at a point of the ground (map tiles gx, gy; screen pixel x, y),
 * for the ground painter: the same patterns as the sprites' paving, a pixel at a time.
 */
export function pavingRGB(
  s: TerraceStyle,
  gx: number,
  gy: number,
  x: number,
  y: number,
): [number, number, number] {
  const F = hexRgb(s.floor);
  const J = hexRgb(s.joint);
  const mul = (c: readonly number[], k: number) =>
    [c[0]! * k, c[1]! * k, c[2]! * k] as [number, number, number];
  const grain = 1 + (hash(x, y) - 0.5) * 0.05;
  if (s.paving === 'grid' || s.paving === 'snow') {
    const u = gx * 2;
    const v = gy * 2;
    const fu = u - Math.floor(u);
    const fv = v - Math.floor(v);
    if (s.paving === 'snow') {
      const n = valueNoise(gx * 0.32 + 3, gy * 0.32 + 7) * 0.8 + valueNoise(gx * 1.3, gy * 1.3 + 5) * 0.2;
      if (n > 0.55) return hexRgb(n > 0.66 ? SNOW : SNOW_S).map((c) => c * grain) as [number, number, number];
    }
    if (fu < 0.08 || fv < 0.08) return mul(J, grain);
    return mul(F, (0.9 + hash(Math.floor(u), Math.floor(v)) * 0.16) * grain);
  }
  if (s.paving === 'bond') {
    const row = Math.floor(gy / 0.375);
    const fv = gy / 0.375 - row;
    const u = gx / 0.75 + (row % 2) * 0.5;
    const fu = u - Math.floor(u);
    if (fu < 0.05 || fv < 0.1) return mul(J, grain);
    return mul(F, (0.9 + hash(Math.floor(u), row) * 0.16) * grain);
  }
  if (s.paving === 'crazy') {
    // Flagstones of every shape: the nearest of a scatter of points, a joint where two meet.
    const k = 0.6;
    const ci = Math.floor(gx / k);
    const cj = Math.floor(gy / k);
    let d1 = 9;
    let d2 = 9;
    let id = 0;
    for (let b = cj - 1; b <= cj + 1; b++)
      for (let a = ci - 1; a <= ci + 1; a++) {
        const px = (a + 0.2 + hash(a, b) * 0.6) * k;
        const py = (b + 0.2 + hash(b + 7, a) * 0.6) * k;
        const d = Math.hypot(gx - px, gy - py);
        if (d < d1) [d2, d1, id] = [d1, d, hash(a * 3, b * 5)];
        else if (d < d2) d2 = d;
      }
    if (d2 - d1 < 0.05) return hash(x * 5, y) > 0.6 ? [90, 118, 58] : mul(J, grain);
    return mul(F, (0.86 + id * 0.22) * grain);
  }
  // Marble: great slabs, a fine joint, faint grey veins.
  const fu = gx - Math.floor(gx);
  const fv = gy - Math.floor(gy);
  if (fu < 0.04 || fv < 0.04) return mul(J, grain);
  const vein = Math.abs(valueNoise(gx * 2.2 + 5, gy * 2.2) - 0.5) < 0.025;
  return mul(F, (vein ? 0.86 : 0.97 + hash(Math.floor(gx), Math.floor(gy)) * 0.06) * grain);
}
